import React from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { Feature } from '@techstyle/react-features';
import { FormattedMessage } from '@techstyle/react-intl';

import { mobile } from '../../styles';
import { getLinkedProduct, isUnisex } from '../../utils/productDetail';
import { getAvailableQuantity } from '../../utils/productQuantity';
import { isOneSize, hasBraSize } from '../../utils/sizes';
import { Component as ProductDetailSizeSelector } from '../ProductDetailSizeSelector';
import ProductSizeLabel, {
  useGetProductSizeLabel,
} from '../ProductSizeLabel/ProductSizeLabel';
import QuantityCounterSelect from '../QuantityCounterSelect/QuantityCounterSelect';

const SizeContainer = styled.div`
  ${mobile`
    margin-top: ${props => props.theme.spacing.small}px
  `};
  width: 100%;

  ${QuantityCounterSelect.QuantityValue} {
    margin-top: 3px;
  }
`;

const QuantitySizeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
`;

const SizeSelectContainer = styled.div`
  width: 100%;
`;

const UnisexTextContainer = styled.div`
  width: 100%;
  margin-bottom: ${props => props.theme.spacing.medium}px;
  font-size: 12px;
  font-weight: 300;
  letter-spacing: 0.3px;
  line-height: 14px;

  ${mobile`line-height: 16px;`}
`;

const ProductDetailSizeSelectorWrapper = styled.div`
  ${ProductDetailSizeSelector.SizeCategoryWrapper} {
    ${ProductDetailSizeSelector.SizeGuideButton} {
      @media (min-width: ${props => props.theme.breakpoints.xxlarge}px) {
        ${props =>
          !props.shouldProductFitReviewAlignToRight && 'margin-right: 12px'};
      }

      padding-bottom: 0;
    }
  }

  ${ProductDetailSizeSelector.SizeCategoryLabel} {
    @media (min-width: ${props => props.theme.breakpoints.xxlarge}px) {
      padding-bottom: ${props => props.theme.spacing.tiny}px;
    }

    ${props =>
      props.shouldProductFitReviewAlignToRight &&
      css`
        align-items: end;
      `}
  }

  ${ProductDetailSizeSelector.SizeCategoryLabel}:first-child {
    ${props =>
      props.shouldProductFitReviewAlignToRight &&
      css`
        @media (min-width: ${props =>
            props.theme.breakpoints.large}px) and (max-width: ${props =>
            props.theme.breakpoints.xxlarge}px) {
          margin-top: ${props => props.theme.spacing.large}px;
        }
      `}
  }

  ${ProductDetailSizeSelector.ProductFitReview} {
    ${props =>
      props.shouldProductFitReviewAlignToRight &&
      css`
        align-items: flex-end;
        flex-flow: column-reverse;
      `}
  }

  ${ProductDetailSizeSelector.FitLabel} {
    ${props =>
      props.shouldProductFitReviewAlignToRight &&
      css`
        color: ${props => props.theme.colors.default};
        font-weight: 500;
        margin-right: 0;
        text-align: right;
      `}
  }

  ${ProductDetailSizeSelector.FitLink} {
    ${props =>
      !props.shouldProductFitReviewAlignToRight &&
      css`
        ${mobile`margin-right: 10px;`}

        margin-right: 12px;
      `}

    @media (min-width: ${props =>
      props.theme.breakpoints.large}px) and (max-width: ${props =>
      props.theme.breakpoints.xxlarge}px) {
      margin-right: ${props =>
        props.shouldProductFitReviewAlignToRight ? '0' : '12px'};
    }

    ${props =>
      props.shouldProductFitReviewAlignToRight &&
      css`
        margin-right: 0;
      `}
  }
`;

class ProductDetailSize extends React.PureComponent {
  static displayName = 'ProductDetailSize';
  static propTypes = {
    className: PropTypes.string,
    error: PropTypes.string,
    getProductSizeLabel: PropTypes.func.isRequired,
    isSoldOut: PropTypes.bool,
    hideQuantity: PropTypes.bool,
    id: PropTypes.number,
    isPreorderable: PropTypes.bool,
    reviews: PropTypes.object,
    isGwpSelection: PropTypes.bool,
    isSet: PropTypes.bool,
    onChangeSize: PropTypes.func,
    onChangeQuantity: PropTypes.func,
    masterProduct: PropTypes.object.isRequired,
    selectedProductQuantity: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]).isRequired,
    setIndex: PropTypes.number,
    size: PropTypes.object.isRequired,
    sizeList: PropTypes.array,
    isCombinedSet: PropTypes.bool,
    trackSizeChange: PropTypes.func,
    setId: PropTypes.number,
    shouldHideFitAndSizeGuide: PropTypes.bool,
    shouldProductFitReviewAlignToRight: PropTypes.bool,
    shouldSplitCombinedSize: PropTypes.bool,
  };

  static defaultProps = {
    isSoldOut: false,
    isSet: false,
    isCombinedSet: false,
    isGwpSelection: false,
    sizeList: [],
    setId: null,
    shouldHideFitAndSizeGuide: false,
  };

  componentDidUpdate(prevProps) {
    const {
      size,
      trackSizeChange,
      setId,
      masterProduct,
      isPreorderable,
      reviews,
    } = this.props;

    const {
      average_overall_review_filtered_stores: averageRating = 0,
      meta_data: metaData = [],
    } = masterProduct;

    const hasMemberModels = metaData.some(metaData =>
      Boolean(metaData.meta_description_override)
    );

    const oldSize = prevProps.size.sizeProduct || '';
    const { sizeProduct } = size;
    const { counts = {} } = reviews;

    const { reviewCount = 0, recommendedPercentage = 0 } = counts;

    const sharedTracking = {
      is_preorder: isPreorderable,
      avg_recommended: recommendedPercentage,
      review_num: reviewCount,
      has_reviews: Boolean(reviewCount),
      avg_star_rating: Number(averageRating.toFixed(1)),
      has_member_models: hasMemberModels,
    };
    if (!oldSize && sizeProduct && sizeProduct.label_instance) {
      trackSizeChange({
        ...sharedTracking,
        label: `none to ${sizeProduct.label_instance}`,
        product_id: setId
          ? setId.toString()
          : masterProduct.master_product_id.toString(),
      });
    } else if (
      oldSize &&
      sizeProduct &&
      oldSize.label_instance !== sizeProduct.label_instance
    ) {
      trackSizeChange({
        ...sharedTracking,
        label: `${oldSize.label_instance} to ${sizeProduct.label_instance}`,
        product_id: setId
          ? setId.toString()
          : masterProduct.master_product_id.toString(),
      });
    }
  }

  handleSizeChange = (sizeId, field) => {
    const { id, onChangeSize } = this.props;
    onChangeSize && onChangeSize(sizeId, id, field);
  };

  handleQuantityChange = newQuantity => {
    const { onChangeQuantity } = this.props;
    onChangeQuantity && onChangeQuantity(newQuantity);
  };

  getSizeLabel = sizeProduct => {
    const { masterProduct, getProductSizeLabel, sizeList } = this.props;
    const isOneSizeItem = isOneSize(masterProduct);
    const shouldDisplayMultipleOneSizes = isOneSizeItem && sizeList.length > 1;

    return getProductSizeLabel({
      masterProduct,
      isOneSize: isOneSizeItem,
      product: sizeProduct,
      shouldDisplayMultipleOneSizes,
    });
  };

  getSizeOptions() {
    const { masterProduct, sizeList, isSoldOut, isPreorderable } = this.props;

    const linkedMasterProduct = getLinkedProduct(masterProduct);
    const linkedProduct = linkedMasterProduct || null;
    const isOneSizeItem = isOneSize(masterProduct);

    return sizeList.map(sizeItem => {
      const oneSizeProductId = isOneSizeItem && sizeItem.product_id;

      return {
        id: sizeItem.size,
        value: sizeItem.size,
        label: this.renderSizeLabel(sizeItem, isOneSizeItem, masterProduct),
        labelString: this.getSizeLabel(sizeItem),
        productId:
          sizeItem.is_linked_size && linkedProduct && !isOneSizeItem
            ? linkedProduct.master_product_id
            : oneSizeProductId || sizeItem.productId,
        permalink:
          sizeItem.is_linked_size && linkedProduct
            ? linkedProduct.permalink
            : sizeItem.permalink,
        isLinkedSize: sizeItem.is_linked_size,
        isSoldOut: isSoldOut
          ? true
          : getAvailableQuantity(sizeItem, masterProduct, isPreorderable) <= 0,
        isOneSize: isOneSizeItem,
      };
    });
  }

  renderSizeLabel(sizeItem, isOneSize = false, masterProductDetails = {}) {
    const { sizeList } = this.props;
    const shouldDisplayMultipleOneSizes = isOneSize && sizeList.length > 1;

    const label = (
      <ProductSizeLabel
        product={sizeItem}
        isOneSize={isOneSize}
        masterProduct={masterProductDetails}
        shouldDisplayMultipleOneSizes={shouldDisplayMultipleOneSizes}
      />
    );

    return <span data-autotag="pdp-size-option">{label}</span>;
  }

  render() {
    const {
      className,
      error: productError,
      hideQuantity,
      isPreorderable,
      isSoldOut,
      isSet,
      masterProduct,
      minimumQuantity,
      selectedColorLabel,
      selectedProductQuantity,
      size,
      innerRef,
      isCombinedSet,
      isGwpSelection,
      shouldHideFitAndSizeGuide,
      shouldProductFitReviewAlignToRight,
      shouldSplitCombinedSize,
      setIndex,
    } = this.props;
    const availableQuantity = isSoldOut
      ? 0
      : getAvailableQuantity(size.sizeProduct, masterProduct, isPreorderable);

    const isProductUnisex = isUnisex(masterProduct);

    return (
      <SizeContainer ref={innerRef} className={className}>
        <QuantitySizeContainer>
          <SizeSelectContainer data-autotag="pdp-size-options">
            <Feature handle="bottom_size_and_fit_guide">
              {({ data, networkStatus }) => {
                const isFeatureEnabled =
                  networkStatus.isUpToDate &&
                  !networkStatus.isLoading &&
                  data &&
                  data.value;
                let shouldShowBottomSizeAndFitGuide = false;

                if (isFeatureEnabled) {
                  shouldShowBottomSizeAndFitGuide = true;
                }

                return (
                  <ProductDetailSizeSelectorWrapper
                    shouldProductFitReviewAlignToRight={
                      shouldProductFitReviewAlignToRight
                    }
                  >
                    <ProductDetailSizeSelector
                      label={
                        <FormattedMessage
                          id="site_pdp.size_label"
                          defaultMessage="Size"
                        />
                      }
                      shouldHideFitAndSizeGuide={shouldHideFitAndSizeGuide}
                      shouldShowBottomSizeAndFitGuide={
                        shouldShowBottomSizeAndFitGuide
                      }
                      displayBraSize={hasBraSize(masterProduct)}
                      error={productError}
                      product={masterProduct}
                      sizes={this.getSizeOptions()}
                      selectedSize={size}
                      selectedColorLabel={selectedColorLabel}
                      onChooseSize={this.handleSizeChange}
                      isProductSet={isSet}
                      isCombinedSet={isCombinedSet}
                      isGwpSelection={isGwpSelection}
                      isProductUnisex={isProductUnisex}
                      shouldHideProductFitReviewPipe={
                        shouldProductFitReviewAlignToRight
                      }
                      shouldSplitCombinedSize={shouldSplitCombinedSize}
                      getSizeLabel={this.getSizeLabel}
                      setIndex={setIndex}
                    />
                  </ProductDetailSizeSelectorWrapper>
                );
              }}
            </Feature>
          </SizeSelectContainer>

          {isProductUnisex ? (
            <UnisexTextContainer>
              <FormattedMessage
                id="site_pdp.unisex_text"
                defaultMessage="This item is for every Body. Find your size on our size guide."
              />
            </UnisexTextContainer>
          ) : null}

          {isSoldOut || hideQuantity ? null : (
            <QuantityCounterSelect
              availableQuantity={availableQuantity}
              minimumQuantity={minimumQuantity}
              onChange={this.handleQuantityChange}
              value={selectedProductQuantity}
            />
          )}
        </QuantitySizeContainer>
      </SizeContainer>
    );
  }
}

// This is a wrapper that allows us to use hooks in a class component, we need this to be able to
// derive the product size label and use it in the selector.
function ProductDetailSizeWithHooks(props) {
  const { getProductSizeLabel } = useGetProductSizeLabel();

  return (
    <ProductDetailSize {...props} getProductSizeLabel={getProductSizeLabel} />
  );
}

ProductDetailSizeWithHooks.displayName = 'ProductDetailSizeWithHooks';

ProductDetailSizeWithHooks.propTypes = {
  ...ProductDetailSize.propTypes,
};

export default ProductDetailSizeWithHooks;
